import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import pt from "./locales/pt.json";
import en from "./locales/en.json";
import es from "./locales/es.json";

export type Language = "pt" | "en" | "es";

const STORAGE_KEY = "language";

function detectInitialLanguage(): Language {
  const saved = localStorage.getItem(STORAGE_KEY) as Language | null;
  const envDefault =
    (import.meta.env.VITE_DEFAULT_LANGUAGE as Language) || "pt";
  const candidate = saved || envDefault || "pt";
  return (["pt", "en", "es"] as Language[]).includes(candidate)
    ? candidate
    : "pt";
}

// Flat-key JSON translations matching the Vue source format (no namespaces).
// {var} interpolation is preserved via i18next's interpolation config.
void i18n.use(initReactI18next).init({
  resources: {
    pt: { translation: pt as Record<string, string> },
    en: { translation: en as Record<string, string> },
    es: { translation: es as Record<string, string> },
  },
  lng: detectInitialLanguage(),
  // Produção cai no português para nunca mostrar a chave crua ao usuário. O
  // efeito colateral é que toda falta de tradução vira português silencioso —
  // por isso o missingKeyHandler abaixo, que torna a falta audível em dev.
  fallbackLng: "pt",
  interpolation: {
    escapeValue: false,
    prefix: "{",
    suffix: "}",
  },
  returnNull: false,
  saveMissing: import.meta.env.DEV,
  missingKeyHandler: import.meta.env.DEV
    ? (lngs, _ns, key, fallbackValue) => {
        // Dispara quando a chave não existe no idioma pedido. Se a chamada usou
        // t("chave", "Texto PT"), esse texto vira o fallback em QUALQUER idioma
        // — é assim que português vaza para EN/ES sem ninguém notar.
        console.warn(
          `[i18n] chave ausente em "${lngs?.join?.(", ") ?? lngs}": ${key}` +
            (fallbackValue ? ` (usando defaultValue "${fallbackValue}")` : ""),
        );
      }
    : undefined,
});

export async function changeLanguage(lang: Language) {
  if (!["pt", "en", "es"].includes(lang)) lang = "pt";
  localStorage.setItem(STORAGE_KEY, lang);
  await i18n.changeLanguage(lang);

  // Revalida o catálogo de conteúdo do cadastro: trocar de idioma é o momento
  // em que o usuário repara numa tradução errada, então é quando mais vale
  // conferir se o servidor tem uma versão nova. Import dinâmico para o store
  // não entrar no grafo de módulos do boot (o i18n é importado antes dele).
  void import("@/stores/dataI18nStore")
    .then((m) => m.useDataI18nStore.getState().reload())
    .catch(() => {
      /* catálogo é opcional: sem ele o texto original é exibido */
    });
}

export function getLanguage(): Language {
  const lang = (i18n.language || "pt") as Language;
  return (["pt", "en", "es"] as Language[]).includes(lang) ? lang : "pt";
}

// Framework-agnostic translation helper (for use in services / outside React).
export function t(
  key: string,
  params?: Record<string, string | number>,
): string {
  return i18n.t(key, params as any) as string;
}

export default i18n;
